import {
  Ticket, PlusCircle, BookOpen, BarChart3, Settings, Shield, ClipboardCheck,
  Users, Building2, TrendingUp, Activity, LayoutGrid, Inbox, CalendarCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────
   Hub-and-spoke navigation config — the Home tiles (replacing the
   old sidebar) and the global back-bar parent fallback map.
───────────────────────────────────────────────────────────── */

export interface NavTile {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** only shown to super-admins */
  superOnly?: boolean;
  group: 'Work' | 'CRM' | 'Account';
}

export const STAFF_TILES: NavTile[] = [
  { to: '/tickets',           label: 'Tickets',        description: 'View, filter and resolve support tickets.', icon: Ticket,        group: 'Work' },
  { to: '/my-work',           label: 'My Work',        description: 'Tasks assigned to you, and your own list.',  icon: ClipboardCheck, group: 'Work' },
  { to: '/tickets/new',       label: 'New Ticket',     description: 'Open a new support ticket.',                icon: PlusCircle,    group: 'Work' },
  { to: '/kb',                label: 'Knowledge Base', description: 'Browse and edit help articles.',            icon: BookOpen,      group: 'Work' },
  { to: '/reports',           label: 'Reports',        description: 'Volume, SLA and performance analytics.',    icon: BarChart3,     group: 'Work' },

  { to: '/crm',               label: 'CRM Overview',   description: 'Inbox, leads and pipeline at a glance.',    icon: TrendingUp,    group: 'CRM' },
  { to: '/crm/inbox',         label: 'Inbox',          description: 'Sales email and contact-form leads.',       icon: Inbox,         group: 'CRM' },
  { to: '/crm/work',          label: 'My Work',        description: 'What is due, overdue, and undecided.',      icon: CalendarCheck, group: 'CRM' },
  { to: '/crm/contacts',      label: 'Contacts',       description: 'People you do business with.',              icon: Users,         group: 'CRM' },
  { to: '/crm/organizations', label: 'Organizations',  description: 'Companies and accounts.',                   icon: Building2,     group: 'CRM' },
  { to: '/crm/deals/board',   label: 'Deals',          description: 'Track opportunities through the pipeline.', icon: LayoutGrid,    group: 'CRM' },
  { to: '/crm/activities',    label: 'Activities',     description: 'Calls, emails, meetings and tasks.',        icon: Activity,      group: 'CRM' },

  // No "My Profile" tile: the header's user menu owns Profile everywhere in
  // the app, and a tile for it duplicated that in one place only.
  { to: '/settings/company',  label: 'Company Settings', description: 'Teams, SLAs, members, integrations.',     icon: Settings,      group: 'Account' },
  { to: '/admin',             label: 'Admin',          description: 'Manage users, tenants and roles.',          icon: Shield,        group: 'Account', superOnly: true },
];

export const PORTAL_TILES: NavTile[] = [
  { to: '/portal/tickets',     label: 'My Tickets',     description: 'Track the tickets you have opened.',  icon: Ticket,     group: 'Work' },
  { to: '/portal/tickets/new', label: 'New Ticket',     description: 'Ask for help or report an issue.',    icon: PlusCircle, group: 'Work' },
  { to: '/portal/kb',          label: 'Knowledge Base', description: 'Find answers in our help articles.',  icon: BookOpen,   group: 'Work' },
  { to: '/portal/join',        label: 'Join a Company', description: 'Connect to another company.',         icon: Building2,  group: 'Account' },
];

/** Routes that are hubs — the global back bar is hidden on these. */
export const HOME_PATHS = new Set(['/', '/portal']);

/** Parent fallback for a direct page load with no in-app history. */
const FALLBACK: [RegExp, string][] = [
  [/^\/tickets\/.+/,                '/tickets'],
  [/^\/kb\/.+/,                     '/kb'],
  [/^\/crm\/inbox\/.+/,             '/crm/inbox'],
  [/^\/crm\/contacts\/.+/,          '/crm/contacts'],
  [/^\/crm\/organizations\/.+/,     '/crm/organizations'],
  [/^\/crm\/deals\/.+/,             '/crm'],
  [/^\/crm\/.+/,                    '/crm'],
  [/^\/portal\/tickets\/.+/,        '/portal/tickets'],
  [/^\/portal\/kb\/.+/,             '/portal/kb'],
  [/^\/portal\/join\/.+/,           '/portal/join'],
  [/^\/portal\/.+/,                 '/portal'],
  [/^\/.+/,                         '/'],
];

export function parentPath(pathname: string): string {
  for (const [re, parent] of FALLBACK) if (re.test(pathname)) return parent;
  return pathname.startsWith('/portal') ? '/portal' : '/';
}
